<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { iaReportsAPI } from 'dashboard/api/iaReports';

const today = new Date();
const defaultFrom = new Date(today);
defaultFrom.setDate(today.getDate() - 29);

const fromDate  = ref(defaultFrom.toISOString().slice(0, 10));
const toDate    = ref(today.toISOString().slice(0, 10));
const search    = ref('');
const page      = ref(1);

const loading  = ref(false);
const error    = ref(null);
const payload  = ref([]);
const meta     = ref({ total: 0, pages: 1 });

// Debounce search
let searchTimer = null;
watch(search, () => {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => { page.value = 1; fetchHandoffs(); }, 400);
});

watch([fromDate, toDate, page], fetchHandoffs);

async function fetchHandoffs() {
  loading.value = true;
  error.value   = null;
  try {
    const res = await iaReportsAPI.getHandoffs({
      from: fromDate.value,
      to:   toDate.value,
      search: search.value || undefined,
      page: page.value,
    });
    payload.value = res.data?.payload ?? [];
    meta.value    = res.data?.meta ?? { total: 0, pages: 1 };
  } catch (e) {
    error.value = e?.response?.data?.error || e.message || 'Erro ao carregar transbordos';
  } finally {
    loading.value = false;
  }
}

onMounted(fetchHandoffs);

function formatTime(seconds) {
  if (!seconds || seconds <= 0) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h > 0 ? `${h}h ${m}min` : `${m}min`;
}

function openConversation(displayId) {
  const accountId = window.location.pathname.split('/')[3];
  const base = window.location.origin;
  window.open(`${base}/app/accounts/${accountId}/conversations/${displayId}`, '_blank');
}
</script>

<template>
  <div class="flex flex-col gap-6 p-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-xl font-bold text-n-slate-12">Registro de Transbordos</h1>
        <p class="text-sm text-n-slate-10 mt-0.5">Acompanhe todas as conversas transferidas da IA para atendimento humano</p>
      </div>

      <!-- Summary badge -->
      <div v-if="!loading" class="px-4 py-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-700/30">
        <p class="text-xs text-amber-700 dark:text-amber-400 font-medium">
          Total no período: <strong>{{ meta.total }}</strong>
        </p>
      </div>
    </div>

    <!-- Filters -->
    <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
      <!-- Search -->
      <div class="relative flex-1">
        <span class="absolute inset-y-0 left-3 flex items-center pointer-events-none">
          <span class="i-lucide-search w-4 h-4 text-n-slate-9" />
        </span>
        <input
          v-model="search"
          type="text"
          placeholder="Buscar por cliente..."
          class="w-full pl-9 pr-3 py-2 text-sm bg-white dark:bg-n-alpha-2 border border-n-weak rounded-lg focus:outline-none focus:ring-2 focus:ring-n-brand/30 text-n-slate-12 placeholder:text-n-slate-9"
        />
      </div>

      <!-- Date range -->
      <div class="flex items-center gap-2 bg-white dark:bg-n-alpha-2 border border-n-weak rounded-lg px-3 py-2">
        <span class="i-lucide-calendar w-4 h-4 text-n-slate-10" />
        <input v-model="fromDate" type="date" class="text-sm bg-transparent text-n-slate-12 outline-none" />
        <span class="text-n-slate-9">–</span>
        <input v-model="toDate" type="date" class="text-sm bg-transparent text-n-slate-12 outline-none" />
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center py-16 text-n-slate-9">
      <span class="i-lucide-loader-2 w-5 h-5 animate-spin mr-2" />
      Carregando transbordos...
    </div>

    <!-- Error -->
    <div v-else-if="error" class="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-700/30 text-red-700 dark:text-red-400">
      <span class="i-lucide-alert-circle w-5 h-5 shrink-0" />
      <span class="text-sm">{{ error }}</span>
      <button class="ml-auto text-xs underline" @click="fetchHandoffs">Tentar novamente</button>
    </div>

    <!-- Empty -->
    <div v-else-if="payload.length === 0" class="flex flex-col items-center justify-center py-16 gap-3 text-n-slate-9">
      <span class="i-lucide-arrow-right-left w-10 h-10 opacity-30" />
      <p class="text-sm">Nenhum transbordo registrado neste período.</p>
      <p class="text-xs text-center max-w-xs">
        Ative <strong>"Registrar Transbordos"</strong> nas configurações do agente (aba Operação → Registros & Análise).
      </p>
    </div>

    <!-- Table -->
    <template v-else>
      <div class="overflow-hidden rounded-xl border border-n-weak bg-white dark:bg-n-alpha-2 shadow-sm">
        <table class="w-full text-sm">
          <thead>
            <tr class="bg-gradient-to-r from-n-brand to-blue-600 text-white">
              <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Data</th>
              <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Cliente</th>
              <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Departamento</th>
              <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Atribuído a</th>
              <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Tempo p/ transbordo</th>
              <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Conversa</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <tr
              v-for="item in payload"
              :key="item.id"
              class="hover:bg-n-alpha-1 transition-colors"
            >
              <td class="px-4 py-3 text-n-slate-11 whitespace-nowrap">{{ item.date }}</td>
              <td class="px-4 py-3">
                <p class="font-medium text-n-slate-12">{{ item.contact?.name }}</p>
                <p v-if="item.contact?.phone_number" class="text-xs text-n-slate-9">{{ item.contact.phone_number }}</p>
              </td>
              <td class="px-4 py-3">
                <span v-if="item.team" class="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-n-alpha-3 text-n-slate-11">
                  {{ item.team }}
                </span>
                <span v-else class="text-n-slate-9 text-xs">—</span>
              </td>
              <td class="px-4 py-3 text-n-slate-11">
                {{ item.assignee?.name || '—' }}
              </td>
              <td class="px-4 py-3 text-n-slate-11">
                {{ formatTime(item.time_to_handoff_seconds) }}
              </td>
              <td class="px-4 py-3">
                <button
                  class="flex items-center gap-1 text-xs text-n-brand hover:underline"
                  @click="openConversation(item.conversation_display_id)"
                >
                  <span class="i-lucide-external-link w-3.5 h-3.5" />
                  Ver conversa
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="flex items-center justify-between text-xs text-n-slate-10">
        <span>{{ meta.total }} registros · Página {{ page }} de {{ meta.pages }}</span>
        <div class="flex gap-2">
          <button
            :disabled="page <= 1"
            class="px-3 py-1.5 rounded-lg border border-n-weak disabled:opacity-40 hover:bg-n-alpha-2 transition-colors"
            @click="page--"
          >
            Anterior
          </button>
          <button
            :disabled="page >= meta.pages"
            class="px-3 py-1.5 rounded-lg border border-n-weak disabled:opacity-40 hover:bg-n-alpha-2 transition-colors"
            @click="page++"
          >
            Próxima
          </button>
        </div>
      </div>
    </template>
  </div>
</template>
